import classNames from 'classnames';
import React, { useRef } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { createUseStyles } from 'react-jss';
import variables from '../theme/variables';
import Lottie from './common/Lottie';
import TitleScreenAnimationDesktop from '../assets/lotties/TitleScreenAnimationDesktop.json';
import TitleScreenAnimationMobile from '../assets/lotties/TitleScreenAnimationMobile.json';
import IntroScreenAnimationDesktop from '../assets/lotties/IntroScreenAnimationDesktop.json';
import IntroScreenAnimationMobile from '../assets/lotties/IntroScreenAnimationMobile.json';
import { ReactComponent as ArrowDownIcon } from '../assets/icons/ArrowDownIcon.svg';
import TransitionText from './common/TransitionText';
import AnimatedSplashTitle from './AnimatedSplashTitle';

const useStyles = createUseStyles(
    {
        wrap: {
            position: 'relative',
            width: '100%',
            minHeight: 'var(--app-height)',
            opacity: 1,
            transition: 'opacity 1s ease-out',
        },

        wrapOut: {
            opacity: 0,
        },

        animationWrap: {
            position: 'relative',
            minHeight: 'var(--app-height)',
            overflow: 'hidden',
        },

        animationRatio: {
            position: 'absolute',
            width: '100%',
            paddingBottom: `${(1920 * 100) / 1080}%`,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',

            '@media (min-aspect-ratio: 2880/2048)': {
                paddingBottom: 0,
                height: '100%',
                width: '300vw',
            },
        },

        animation: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            height: 'auto',

            '@media (min-aspect-ratio: 2880/2048)': {
                width: '100%',
                height: '100%',
            },
        },

        animationCover: {
            position: 'fixed',
            // minWidth: '100vw',
            // minHeight: '100vh',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',

            height: '102vh',
            width: '200vh',

            '@media (orientation: landscape)': {
                width: '102vw',
                height: '200vw',
            },
        },

        footer: {
            position: 'absolute',
            bottom: 64,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'calc(100% - 32px)',
            maxWidth: 644,

            textAlign: 'center',
            color: variables.palette.white,
            fontSize: 16,
            lineHeight: '23px',

            '@media (min-width: 1024px)': {
                fontSize: 20,
                lineHeight: '31px',
            },
        },

        footerButton: {
            marginTop: 20,
            fontSize: 24,
            color: variables.palette.white,

            '@media (min-width: 1024px)': {
                fontSize: 32,
            },
        },

        skipButtonWrap: {
            position: 'fixed',
            top: `${variables.msftHeaderHeightMobile + 36}px`,
            left: 14,

            '@media (min-width: 860px)': {
                top: `${variables.msftHeaderHeight + 46}px`,
                left: 60,
            },
        },

        skipButton: {
            color: variables.palette.white,
            fontSize: 18,
            fontWeight: 400,
            textDecoration: 'underline',
            textUnderlineOffset: '0.25em',
            textDecorationThickness: 1,
        },
    },
    { name: 'Intro' }
);

export default function Intro({ onBegin }) {
    const classes = useStyles();
    const [state, setState] = useState('title'); // title | intro | out

    const [aspect, setAspect] = useState(); // portrait | landscape

    const [splashState, setSplashState] = useState('init'); // init | in | out
    const [splashPaused, setSplashPaused] = useState(false);
    const [splashNextButtonState, setSplashNextButtonState] = useState('init'); // init | in | out

    const animationWrapRef = useRef();
    const [skipButtonState, setSkipButtonState] = useState('init');
    const [introPaused, setIntroPaused] = useState(false);
    const [enterButtonState, setEnterButtonState] = useState('init'); // init | in | out

    useEffect(() => {
        function calcAspect() {
            if (animationWrapRef.current) {
                let w = animationWrapRef.current.offsetWidth;
                let h = animationWrapRef.current.offsetHeight;

                if (w / h < 2880 / 2048) {
                    setAspect('portrait');
                } else {
                    setAspect('landscape');
                }
            }
        }

        calcAspect();
        window.addEventListener('resize', calcAspect);

        return () => {
            window.removeEventListener('resize', calcAspect);
        };
    }, [animationWrapRef.current]);

    const handleStartIntro = () => {
        setSplashPaused(false);
        setSplashNextButtonState('out');
        setSplashState('out');
        // TODO - remove - trigger on animation ended as usual
        setTimeout(() => {
            setState('intro');
            setSkipButtonState('in');
        }, 1000);
    };

    const handleSkipIntro = () => {
        setState('out');
        setTimeout(() => onBegin(), 1001);
    };

    const handleEnterstudio = () => {
        setIntroPaused(false);
        setTimeout(() => {
            setState('out');
            setTimeout(() => {
                onBegin();
            }, 500);
        }, 500);
    };

    return (
        <div
            className={classNames(
                classes.wrap,
                state === 'out' ? classes.wrapOut : ''
            )}
        >
            <div className={classes.animationWrap} ref={animationWrapRef}>
                {state === 'title' && (
                    <>
                        <div
                            style={{
                                display:
                                    aspect === 'portrait' ? 'block' : 'none',
                            }}
                        >
                            <Lottie
                                className={classes.animationCover}
                                animationData={TitleScreenAnimationMobile}
                                loop={false}
                                paused={splashPaused}
                                onFrame={[
                                    {
                                        frame: 120,
                                        callback: () => {
                                            setSplashPaused(true);
                                        },
                                    },
                                ]}
                            />
                        </div>
                        <div
                            style={{
                                display:
                                    aspect === 'portrait' ? 'none' : 'block',
                            }}
                        >
                            <Lottie
                                className={classes.animationCover}
                                animationData={TitleScreenAnimationDesktop}
                                loop={false}
                                paused={splashPaused}
                            />
                        </div>
                        <AnimatedSplashTitle
                            state={splashState}
                            setState={setSplashState}
                            onComplete={() => setSplashNextButtonState('in')}
                        />
                    </>
                )}

                {['intro', 'out'].includes(state) && (
                    <>
                        <div
                            className={classes.animationRatio}
                            style={{
                                display:
                                    aspect === 'portrait' ? 'block' : 'none',
                            }}
                        >
                            <Lottie
                                className={classes.animation}
                                animationData={IntroScreenAnimationMobile}
                                loop={false}
                                paused={introPaused}
                                onFrame={[
                                    {
                                        frame: 390,
                                        callback: () => {
                                            setEnterButtonState('in');
                                            setIntroPaused(true);
                                        },
                                    },
                                ]}
                                onCompleteCb={() => {
                                    onBegin();
                                }}
                            />
                        </div>
                        <div
                            className={classes.animationRatio}
                            style={{
                                display:
                                    aspect === 'portrait' ? 'none' : 'block',
                            }}
                        >
                            <Lottie
                                className={classes.animation}
                                animationData={IntroScreenAnimationDesktop}
                                loop={false}
                                paused={introPaused}
                            />
                        </div>
                    </>
                )}
            </div>
            {skipButtonState === 'in' && (
                <div className={classes.skipButtonWrap}>
                    <button
                        className={classes.skipButton}
                        onClick={handleSkipIntro}
                    >
                        Skip Intro
                    </button>
                </div>
            )}
            <div className={classes.footer}>
                <TransitionText
                    state={splashNextButtonState}
                    duration={splashNextButtonState === 'out' ? 500 : 1500}
                    easing="cubic-bezier(0.16, 1, 0.3, 1)"
                >
                    <p>
                        Inside the vibrant mind of Sol LeWitt — the father of
                        Conceptual art.
                    </p>
                    <button
                        className={classes.footerButton}
                        onClick={handleStartIntro}
                    >
                        <ArrowDownIcon />
                    </button>
                </TransitionText>

                <TransitionText
                    state={enterButtonState}
                    duration={enterButtonState === 'out' ? 500 : 1500}
                    easing="cubic-bezier(0.16, 1, 0.3, 1)"
                >
                    <button
                        className={classes.footerButton}
                        onClick={handleEnterstudio}
                    >
                        <p style={{ marginBottom: 30 }}>Enter the studio</p>
                        <ArrowDownIcon />
                    </button>
                </TransitionText>
            </div>
        </div>
    );
}
